import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { toast } from 'react-toastify';
import { Building2, Check } from 'lucide-react';

import { MinimalPlateformeDto } from 'api/plateforme';
import { Button } from 'components/ui/Button';
import { plateformesAPI } from 'services/swagger-api';
import { parseApiError } from 'shared/utils/apiValidationErrors';

import { PLATEFORME_CHOICE_PENDING_KEY, markChoiceDone } from './plateformeChoiceMarker';

const frontUrl = process.env.REACT_APP_FRONT_URL;

interface PlateformeChooserProps {
  /** Plateformes standard du compte (réponse de la connexion standard). */
  plateformes: MinimalPlateformeDto[];
}

/**
 * Choix de la plateforme après une connexion standard, quand le compte en a
 * plusieurs. Le choix est enregistré puis la page est rechargée sur la
 * destination prévue (rôles, portails et thème repartent de la plateforme
 * choisie, comme avec le sélecteur existant).
 */
export const PlateformeChooser = ({ plateformes }: PlateformeChooserProps) => {
  const auth = useAuth();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleContinue = async () => {
    if (!selectedId) return;
    setIsSaving(true);
    try {
      await plateformesAPI.switchCurrentPlateforme({ plateformeId: selectedId });
      markChoiceDone(auth.user?.profile.sub);
      sessionStorage.removeItem(PLATEFORME_CHOICE_PENDING_KEY);
      window.location.replace(window.location.pathname + window.location.search);
    } catch (error) {
      console.error('[choix plateforme]', error);
      const parsed = await parseApiError(error);
      if (parsed.message) toast.error(parsed.message);
      setIsSaving(false);
    }
  };

  // Même séquence que la déconnexion du menu utilisateur.
  const handleLogout = async () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      await auth.signoutRedirect({
        post_logout_redirect_uri: `${frontUrl}/bord`,
      });
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      auth.removeUser();
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = `${frontUrl}/bord`;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Choisissez votre plateforme
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Votre compte est rattaché à plusieurs plateformes.
          </p>
        </div>

        <div className="space-y-3">
          {plateformes.map((plateforme) => {
            const selected = plateforme.id === selectedId;
            return (
              <button
                key={plateforme.id}
                type="button"
                onClick={() => setSelectedId(plateforme.id)}
                aria-pressed={selected}
                className={`flex w-full items-center gap-3 rounded-xl border bg-white p-4 text-left shadow-sm transition-colors ${
                  selected
                    ? 'border-primary ring-1 ring-primary/30'
                    : 'border-gray-200 hover:border-primary/40'
                }`}
              >
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Building2 size={20} />
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
                  {plateforme.name}
                </span>
                {selected && <Check size={18} className="flex-shrink-0 text-primary" />}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col items-center gap-4">
          <Button
            color="primary"
            className="w-full"
            disabled={!selectedId || isSaving}
            onClick={handleContinue}
          >
            Continuer
          </Button>
          <button
            type="button"
            onClick={handleLogout}
            className="text-sm text-gray-500 underline hover:text-gray-700"
          >
            Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
};
